import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import styled from 'styled-components'
import { rem } from 'polished'
import ModalBase from 'LuxKit/Modal/ModalBase'
import TextButton from 'LuxKit/Button/TextButton'
import Clickable from 'components/Common/Clickable'
import Group from 'components/utils/Group'
import Pill from 'LuxKit/Pill'
import { mediaQueryUp } from 'components/utils/breakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CharterBundleModalUspItem from '../../MaldivesCharter/MaldivesCharterUsp/CharterBundleModalUspItem'
import CharterHero from './CharterHero'
import { MaldivesUspGroup1, MaldivesUspGroup2 } from 'components/MaldivesCharter/MaldivesCharterUsp/MaldivesCharterUspItems'
import { MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF } from 'constants/charterFlight'
import config from 'constants/config'

const StyledHeroImage = styled(CharterHero)`
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: linear-gradient(270deg, rgba(3, 106, 192, 0.00) 40%, #036AC0 50%);
    pointer-events: none;
  }
`

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(20)};
  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`

const FullWidthBreakpoint = styled(CSSBreakpoint)`
  width: 100%;
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(16)};
  align-items: start;
`

const heroImage = {
  id: 'maldives-hotel-flight-bundle-marketing-banner-image-large',
  title: 'Maldives Flight Bundle Marketing Banner',
}

function CharterBundleLearnMoreModal() {
  const intl = useIntl()

  const exclusiveBadge = intl.formatMessage({
    id: '0H6n4MaUmL',
    defaultMessage: 'Exclusive to Luxury Escapes',
    description: 'Badge text for charter bundle learn more modal indicating exclusivity',
  })

  const paradiseHeading = intl.formatMessage({
    id: 'I/4Na6p2Fi',
    defaultMessage: 'Less time flying, more time in paradise',
    description: 'Heading for Maldives charter bundle benefits section',
  })

  return (
    <ModalBase dismissible size="L">
      <ModalHeader title="" isOverlay />
      <ModalBody>
        <StyledHeroImage
          id={heroImage.id}
          fit="left"
          gravity="center"
          tabletAspectRatio="16:9"
        >
          <HeroContent>
            <div>
              <Pill kind="primary" variant="success">
                {exclusiveBadge}
              </Pill>
            </div>
            <Heading variant="heading2" colour="neutral-eight">
              <FormattedMessage
                id="qxt/ViDQvS"
                defaultMessage="<line1>Australia's</line1><line2><i>only</i> direct flights</line2>to the Maldives"
                description="Main headline for Maldives charter bundle modal highlighting Australia's exclusive direct flights"
                values={{
                  line1: (chunks: React.ReactNode) => <div>{chunks}</div>,
                  line2: (chunks: React.ReactNode) => <div>{chunks}</div>,
                  i: (chunks: React.ReactNode) => <i>{chunks}</i>,
                }}
              />
            </Heading>
          </HeroContent>
        </StyledHeroImage>

        <ModalContent>
          <VerticalSpacer gap={12}>
            <Heading variant="heading3">
              {paradiseHeading}
            </Heading>
            <VerticalSpacer gap={24}>
              <ContentGrid>
                {MaldivesUspGroup1.map(uspItem => (
                  <CharterBundleModalUspItem
                    key={uspItem.uspId}
                    title={uspItem.title}
                    description={uspItem.description}
                    icon={uspItem.icon}
                    tabletIcon={uspItem.tabletIcon}
                  />),
                )}
              </ContentGrid>
            </VerticalSpacer>
            <VerticalSpacer gap={24}>
              <ContentGrid>
                {MaldivesUspGroup2.map(uspItem => (
                  <CharterBundleModalUspItem
                    key={uspItem.uspId}
                    title={uspItem.title}
                    description={uspItem.description}
                    icon={uspItem.icon}
                    tabletIcon={uspItem.tabletIcon}
                  />),
                )}
              </ContentGrid>
            </VerticalSpacer>
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>

      <ModalFooter>
        <Group direction="vertical" tabletDirection="horizontal" verticalAlign="center" horizontalAlign="center" tabletHorizontalAlign="end" gap={12}>
          <BodyText variant="medium">
            <FormattedMessage
              id="qvglgYsdKp"
              defaultMessage="<strong>Have questions?</strong> Call our priority line"
              description="CTA text encouraging users to call priority support line for charter bundle questions"
              values={{
                strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
              }}
            />
          </BodyText>
          <FullWidthBreakpoint max="mobile">
            <TextButton
              fit="full-width"
              startIcon={<LinePhoneIcon />}
              href={MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF}
              kind="primary"
            >
              {config.MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER}
            </TextButton>
          </FullWidthBreakpoint>
          <CSSBreakpoint min="tablet" as="span">
            <Clickable href={MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF}>
              <Group direction="horizontal" gap={4}>
                <LinePhoneIcon />
                <Heading variant="heading5">
                  {config.MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER}
                </Heading>
              </Group>
            </Clickable>
          </CSSBreakpoint>
        </Group>
      </ModalFooter>
    </ModalBase>
  )
}

export default CharterBundleLearnMoreModal
